import React, { useEffect } from 'react';
import { Breakpoints, Spinner } from '@vw-marketing/us-components';
import { ContentContainer, ContentLayout } from '../styles';
import { StickyHeader } from '../../StickyHeader/StickyHeader';
import { Box, TextBox } from '../../Box';
import { StickyBottomNav } from '../../StickyBottomNav/StickyBottomNav';
import { Section } from '../../Section';
import { useStore } from '../../../store/store';
import { useViewport, ViewportType } from '../../../hooks/use-viewport';
import { getPreviousAndNextSections } from '../../../utils/navigation';
import { NavigationSection, SectionModel } from '../../../fetch-data/graphql';
import { ContentHeader } from './ContentHeader';
import { SlideNavigation } from '../../SlideNavigation/SlideNavigation';
import { useDisclaimer } from '../../Disclaimer';
import { StickyDisclaimerModal } from '../../Disclaimer/StickyDisclaimerModal';
import { SearchContent } from './Search/SearchContent';
import { SearchPagination } from './Search/SearchPagination';
import { Option } from '../../Input/types';
import { usePreviousSearch } from '../../../hooks/custom/use-previous-search';
import { BackButton } from '../../Button/BackButton';
import { useSearchResultsTracking } from '../../../hooks/custom/use-search-results-tracking';
import { useHistory } from '../../../hooks/use-history';
import {
  getModelPathParams,
  normalizeQueryToParams,
  paramsToSearchString,
  getQueryParams,
  normalizeSectionId,
  getSectionFromPath,
} from '../../../utils/query-param-helpers';
import { pushWithPathname } from '../../../utils/navigation-url';

interface ContentProps {
  handleChangeHeaderToggle: (value: boolean) => void;
  openSidebar: () => void;
  goToSection: (sectionId: SectionModel | NavigationSection) => void;
  goToSectionFromSearch: (sectionId: string, searchTerm: string, result: any, resultRelativeIndex: any, totalResults: any) => void;
  isSidebarOpen: boolean;
  scrollToTop: () => void;
  onSearch: (option: Option | null) => void;
}

export const Content = ({
  handleChangeHeaderToggle,
  openSidebar,
  goToSection,
  goToSectionFromSearch,
  onSearch,
  scrollToTop,
}: ContentProps) => {
  const [state] = useStore();
  const history = useHistory();
  const {
    qsgNavigation,
    qsgSectionMap,
    qsgSectionLoading,
    qsgSectionErrors,
    qsgSearchTerm,
    qsgSearchPage,
  } = state.qsg;

  const currentSectionId = getSectionFromPath(history.location.pathname);

  // Track search results loading
  useSearchResultsTracking();

  const { activeDisclaimer, isOpen, closeDisclaimer } = useDisclaimer();

  const viewport = useViewport(
    Breakpoints.b560,
    Breakpoints.b1280,
    ViewportType.DESKTOP
  );
  const isDesktop: boolean = viewport === ViewportType.DESKTOP;
  const isMobile: boolean = viewport === ViewportType.MOBILE;
  const showNavigation = Boolean(qsgNavigation && !isDesktop);

  // Track if we came from search results page
  const { cameFromSearch, markSearchResultClick, restoreSearch } =
    usePreviousSearch({
      qsgSearchTerm,
      qsgSearchPage,
      currentSectionId,
    });

  const currentSection = qsgSectionMap[currentSectionId];
  const isCurrentSectionLoading =
    currentSectionId && qsgSectionLoading[currentSectionId];
  const currentSectionError =
    currentSectionId && qsgSectionErrors[currentSectionId];

  const navSections = qsgNavigation?.sections ?? [];
  const { previous, next } = currentSectionId
    ? getPreviousAndNextSections(navSections, currentSectionId)
    : { previous: null, next: null };

  const handleSectionChange = (section: NavigationSection | null) => {
    if (!section) return;
    goToSection(section);
  };

  const isSearch = Boolean(qsgSearchTerm);

  const isContentLoading = Boolean(
    (!qsgNavigation || isCurrentSectionLoading) && !currentSectionError
  );

  const isContentError = Boolean(currentSectionError);

  const showBackToSearchResultsButton = Boolean(
    cameFromSearch && currentSectionId
  );

  useEffect(() => {
    closeDisclaimer();
  }, [currentSectionId, closeDisclaimer]);

  const handleBack = () => {
    openSidebar();
  };

  const handleBackToSearchResults = () => {
    const searchParams = restoreSearch();
    if (searchParams) {
      // Navigate to navigation path with search params restored
      const pathParams = getModelPathParams(history.location.pathname);
      if (pathParams.modelYear && pathParams.modelId) {
        const navigationPath = `/${pathParams.modelYear}/${pathParams.modelId}`;
        const query = getQueryParams(history);
        const params = normalizeQueryToParams(query);

        params.search = searchParams.searchTerm;
        params.page = searchParams.searchPage.toString();

        const searchString = paramsToSearchString(params);

        pushWithPathname(history, navigationPath, searchString);
        scrollToTop();
      }
    }
  };

  return (
    <ContentLayout>
      {showNavigation && (
        <StickyHeader onChangeToggle={handleChangeHeaderToggle}>
          <ContentHeader onBack={handleBack} />
        </StickyHeader>
      )}

      <ContentContainer>
        {showBackToSearchResultsButton && (
          <BackButton
            onBack={handleBackToSearchResults}
            label='Back to search results'
            ariaLabel='Back to search results'
          />
        )}
        {isSearch ? (
          <SearchContent
            key={`${qsgSearchTerm}:${qsgSearchPage}`}
            goToSectionFromSearch={goToSectionFromSearch}
            onSearch={onSearch}
            isDesktop={isDesktop}
            isMobile={isMobile}
            onSelectSearchResult={markSearchResultClick}
          />
        ) : isContentLoading ? (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Spinner />
          </Box>
        ) : isContentError ? (
          <Box>
            <TextBox>Error loading section: {currentSectionError}</TextBox>
          </Box>
        ) : (
          <>
            {currentSection && (
              <Section
                key={`section-${currentSection.sectionId}`}
                section={currentSection}
              />
            )}
          </>
        )}
      </ContentContainer>

      <StickyDisclaimerModal
        isOpen={isOpen}
        disclaimer={activeDisclaimer}
        onClose={closeDisclaimer}
        hasNavigation={showNavigation}
      />

      {(showNavigation || isSearch) && (
        <StickyBottomNav>
          {isSearch ? (
            <SearchPagination maxVisibleItems={3} showEllipsis={!isMobile} />
          ) : (
            <SlideNavigation
              previous={previous}
              next={next}
              onChange={handleSectionChange}
            />
          )}
        </StickyBottomNav>
      )}
    </ContentLayout>
  );
};
